import { useState } from "react";
import { X, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { api, formatApiError, inr } from "../lib/api";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";
import { ComingSoonBadge } from "./ProductCard";
import { OCCASIONS, FESTIVALS, RECIPIENTS, CATEGORIES } from "../data/taxonomy";

const input = "w-full bg-white border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/70";
const lbl = "text-[10px] font-mono-lux tracking-[0.15em] uppercase text-[#857078] block mb-1";

const parseLines = (t) => t.split("\n").map((s) => s.trim()).filter(Boolean);
const parseKV = (t) => Object.fromEntries(parseLines(t).map((l) => { const i = l.indexOf(":"); return i > 0 ? [l.slice(0, i).trim(), l.slice(i + 1).trim()] : null; }).filter(Boolean));
const kvText = (obj) => Object.entries(obj || {}).map(([k, v]) => `${k}: ${v}`).join("\n");
const variantText = (v) => (v || []).map((x) => `${x.name}: ${(x.options || []).join(", ")}`).join("\n");
const parseVariants = (t) => parseLines(t).map((l) => { const i = l.indexOf(":"); return i > 0 ? { name: l.slice(0, i).trim(), options: l.slice(i + 1).split(",").map((s) => s.trim()).filter(Boolean) } : null; }).filter(Boolean);

export default function ProductEditor({ product, onClose, onSaved }) {
  const isNew = !product;
  const [f, setF] = useState(() => product ? {
    ...product,
    highlightsText: (product.highlights || []).join("\n"),
    specsText: kvText(product.specifications),
    variantsText: variantText(product.variants),
    includedText: (product.whats_included || []).join("\n"),
  } : {
    name: "", slug: "", category: "gift-hampers", mrp: 999, price: 499, stock: 0, sku: "",
    availability: "coming_soon", images: [], description: "", exchange_eligible: false, featured: false,
    personalizable: false,
    occasions: [], festivals: [], recipients: [], highlightsText: "", specsText: "", variantsText: "",
    seo_title: "", seo_description: "", bundle_qty: "", bundle_price: "", includedText: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleTag = (k, slug) => set(k, f[k].includes(slug) ? f[k].filter((s) => s !== slug) : [...f[k], slug]);

  const save = async () => {
    if (!f.name.trim()) return toast.error("Product name is required");
    setSaving(true);
    const body = {
      name: f.name.trim(), category: f.category, mrp: +f.mrp || 0, price: +f.price || 0,
      stock: +f.stock || 0, sku: f.sku, availability: f.availability, images: f.images,
      description: f.description, highlights: parseLines(f.highlightsText),
      specifications: parseKV(f.specsText), variants: parseVariants(f.variantsText),
      whats_included: parseLines(f.includedText),
      occasions: f.occasions, festivals: f.festivals, recipients: f.recipients,
      exchange_eligible: !!f.exchange_eligible, featured: !!f.featured, personalizable: !!f.personalizable,
      seo_title: f.seo_title, seo_description: f.seo_description,
    };
    if (f.bundle_qty && f.bundle_price) body.bundle = { qty: +f.bundle_qty, price: +f.bundle_price, label: `${f.bundle_qty} @ ${inr(f.bundle_price)}` };
    try {
      if (isNew) {
        body.slug = (f.slug || f.name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        await api.post("/admin/products", body);
      } else {
        await api.patch(`/admin/products/${product.slug}`, body);
      }
      toast.success(isNew ? "Product published" : "Product updated — live immediately");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const TagGroup = ({ title, k, items }) => (
    <div>
      <span className={lbl}>{title}</span>
      <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto border border-[#E5E7EB] rounded-lg p-2">
        {items.map((t) => (
          <button type="button" key={t.slug} onClick={() => toggleTag(k, t.slug)}
            className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${f[k].includes(t.slug) ? "bg-[#7E1E3F] text-white border-[#7E1E3F]" : "border-[#E5E7EB] text-[#857078] hover:border-[#7E1E3F]/60"}`}
            data-testid={`tag-${k}-${t.slug}`}>{t.name || t.slug}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="product-editor">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="max-w-5xl mx-auto my-8 bg-[#FBF7F1] rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] bg-white rounded-t-2xl">
          <h2 className="font-serif-lux text-2xl text-[#2E1A22]">{isNew ? "New Product" : `Edit — ${product.name}`}</h2>
          <button onClick={onClose} className="text-[#857078] hover:text-[#2E1A22]" data-testid="editor-close"><X size={20} /></button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 p-6">
          <div className="lg:col-span-2 space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <label><span className={lbl}>Product Name *</span>
                <input className={input} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="editor-name" /></label>
              {isNew && (
                <label><span className={lbl}>Slug (URL)</span>
                  <input className={input} placeholder="auto-generated" value={f.slug} onChange={(e) => set("slug", e.target.value)} data-testid="editor-slug" /></label>
              )}
              <label><span className={lbl}>Category</span>
                <select className={input} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="editor-category">
                  {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select></label>
              <label><span className={lbl}>Availability</span>
                <select className={input} value={f.availability} onChange={(e) => set("availability", e.target.value)} data-testid="editor-availability">
                  <option value="available">AVAILABLE (purchasable)</option>
                  <option value="coming_soon">COMING SOON</option>
                  <option value="out_of_stock">OUT OF STOCK</option>
                </select></label>
              <label><span className={lbl}>Selling Price (₹)</span>
                <input type="number" className={input} value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="editor-price" /></label>
              <label><span className={lbl}>MRP (₹, struck through)</span>
                <input type="number" className={input} value={f.mrp} onChange={(e) => set("mrp", e.target.value)} data-testid="editor-mrp" /></label>
              <label><span className={lbl}>Stock</span>
                <input type="number" className={input} value={f.stock} onChange={(e) => set("stock", e.target.value)} data-testid="editor-stock" /></label>
              <label><span className={lbl}>SKU</span>
                <input className={input} value={f.sku} onChange={(e) => set("sku", e.target.value)} data-testid="editor-sku" /></label>
              <label><span className={lbl}>Bundle Qty (optional)</span>
                <input type="number" className={input} value={f.bundle_qty} onChange={(e) => set("bundle_qty", e.target.value)} data-testid="editor-bundle-qty" /></label>
              <label><span className={lbl}>Bundle Price ₹ (optional)</span>
                <input type="number" className={input} value={f.bundle_price} onChange={(e) => set("bundle_price", e.target.value)} data-testid="editor-bundle-price" /></label>
            </div>
            <div className="flex gap-5">
              <label className="flex items-center gap-2 text-sm text-[#45303A]"><input type="checkbox" className="accent-[#7E1E3F] w-4 h-4" checked={!!f.exchange_eligible} onChange={(e) => set("exchange_eligible", e.target.checked)} data-testid="editor-exchange" /> Exchange eligible</label>
              <label className="flex items-center gap-2 text-sm text-[#45303A]"><input type="checkbox" className="accent-[#7E1E3F] w-4 h-4" checked={!!f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="editor-featured" /> Featured</label>
              <label className="flex items-center gap-2 text-sm text-[#45303A]"><input type="checkbox" className="accent-[#7E1E3F] w-4 h-4" checked={!!f.personalizable} onChange={(e) => set("personalizable", e.target.checked)} data-testid="editor-personalizable" /> Personalizable <span className="text-[10px] text-[#857078]">(asks buyer for name + photo)</span></label>
            </div>
            <label><span className={lbl}>Description</span>
              <textarea rows={3} className={input} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="editor-description" /></label>
            <div className="grid sm:grid-cols-2 gap-4">
              <label><span className={lbl}>Highlights (one per line)</span>
                <textarea rows={4} className={input} value={f.highlightsText} onChange={(e) => set("highlightsText", e.target.value)} data-testid="editor-highlights" /></label>
              <label><span className={lbl}>Specifications (Key: Value per line)</span>
                <textarea rows={4} className={input} value={f.specsText} onChange={(e) => set("specsText", e.target.value)} data-testid="editor-specs" /></label>
              <label><span className={lbl}>Variants (e.g. Size: M, L, XL)</span>
                <textarea rows={3} className={input} value={f.variantsText} onChange={(e) => set("variantsText", e.target.value)} data-testid="editor-variants" /></label>
              <label><span className={lbl}>What's Included (one per line)</span>
                <textarea rows={3} className={input} value={f.includedText} onChange={(e) => set("includedText", e.target.value)} data-testid="editor-included" /></label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <label><span className={lbl}>SEO Title</span>
                <input className={input} value={f.seo_title} onChange={(e) => set("seo_title", e.target.value)} data-testid="editor-seo-title" /></label>
              <label><span className={lbl}>SEO Meta Description</span>
                <input className={input} value={f.seo_description} onChange={(e) => set("seo_description", e.target.value)} data-testid="editor-seo-desc" /></label>
            </div>
            <TagGroup title="Occasions" k="occasions" items={OCCASIONS} />
            <TagGroup title="Festivals" k="festivals" items={FESTIVALS} />
            <TagGroup title="Recipients" k="recipients" items={RECIPIENTS} />
          </div>

          <div className="space-y-4">
            <div>
              <span className={lbl}>Product Images</span>
              <div className="space-y-2">
                {f.images.map((src, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-lg p-2" data-testid={`editor-image-${i}`}>
                    <img src={src} alt="" className="w-12 h-14 object-contain bg-white rounded" />
                    <p className="text-[10px] text-[#857078] flex-1 truncate">{src.split("/").pop().slice(0, 28)}</p>
                    <ImageUploader label="Replace" testid={`editor-replace-${i}`} onUploaded={(url) => set("images", f.images.map((s, j) => j === i ? url : s))} />
                    <button type="button" onClick={() => set("images", f.images.filter((_, j) => j !== i))} className="text-[#E63946] p-1" data-testid={`editor-img-del-${i}`}><Trash2 size={14} /></button>
                  </div>
                ))}
                <ImageUploader label="Add Image" testid="editor-add-image" onUploaded={(url) => set("images", [...f.images, url])} />
              </div>
            </div>
            <div>
              <span className={lbl}>Live Preview</span>
              <div className="card-surface rounded-2xl overflow-hidden" data-testid="editor-preview">
                <div className="relative aspect-[4/5] bg-white">
                  {f.images[0] ? <img src={f.images[0]} alt="" className={`w-full h-full ${f.images[0].startsWith("/assets/") || f.images[0].startsWith("/api/") ? "object-contain p-3" : "object-cover"}`} />
                    : <div className="w-full h-full flex items-center justify-center text-[#857078] text-xs">No image yet</div>}
                  {f.availability !== "available" && <div className="absolute top-3 left-3"><ComingSoonBadge small /></div>}
                </div>
                <div className="p-4">
                  <p className="font-serif-lux text-lg text-[#2E1A22] line-clamp-1">{f.name || "Product name"}</p>
                  <div className="mt-1.5 flex items-baseline gap-2">
                    <span className="text-[#7E1E3F] font-semibold">{inr(f.price || 0)}</span>
                    {+f.mrp > +f.price && <span className="text-xs text-[#857078] line-through">{inr(f.mrp)}</span>}
                  </div>
                  <p className="text-[10px] text-[#857078] mt-2">{CATEGORIES.find((c) => c.slug === f.category)?.name} · {f.availability.replace("_", " ")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#E5E7EB] bg-white rounded-b-2xl">
          <button onClick={onClose} className="text-xs font-semibold tracking-wider uppercase border border-[#E5E7EB] text-[#45303A] rounded-full px-6 py-3 hover:border-[#7E1E3F]" data-testid="editor-cancel">Cancel</button>
          <button onClick={save} disabled={saving}
            className="text-xs font-semibold tracking-wider uppercase bg-[#7E1E3F] text-white rounded-full px-8 py-3 hover:bg-[#5C1230] transition-colors disabled:opacity-60"
            data-testid="editor-publish">{saving ? "Publishing…" : isNew ? "Publish Product" : "Save & Publish"}</button>
        </div>
      </motion.div>
    </div>
  );
}
